import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useV1FormsFormFamiliesRetrieve,
  useV1FormsFormRevisionsList,
  useV1FormsFormRevisionsCreate,
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { PageBanner } from "@/shared/components/PageBanner";
import { useIsDeploymentPhaseActive } from '@/features/tenant-admin/api/deploymentPhaseApi';

export function FormFamilyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission("forms.manage");
  const { data: isDeploymentPhase = false } = useIsDeploymentPhaseActive();

  const { data: family, isLoading } = useV1FormsFormFamiliesRetrieve(id!);
  const { data: revisions, isLoading: isLoadingRevisions } = useV1FormsFormRevisionsList({ family: id } as any);
  const { mutateAsync: createRevision, isPending: isCreating } = useV1FormsFormRevisionsCreate();

  const handleNewRevision = async () => {
    try {
      const res = await createRevision({
        data: { family: id!, definition_json: { title: family?.name || "", layout: { columns: 1 }, fields: [], schema: {} } } as any,
      });
      queryClient.invalidateQueries({ queryKey: ["v1FormsFormRevisionsList"] });
      navigate(`/forms/revisions/${(res as any).id}`);
    } catch (err: any) {
      alert(err.response?.data?.detail || "خطا در ایجاد نسخهٔ جدید");
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 flex-center">
        <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={family?.name || "جزئیات خانواده فرم"}
        subtitle={`کد: ${family?.code || "نامشخص"} | مشاهده و مدیریت نسخه‌های مختلف`}
        icon="📋"
        helpLink="/help/forms/family-detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canManage && (
            <button
              onClick={handleNewRevision}
              disabled={isCreating}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: isCreating ? "not-allowed" : "pointer",
                opacity: isCreating ? 0.7 : 1,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>{isCreating ? "..." : "نسخهٔ جدید"}</span>
            </button>
          )}
          <button
            onClick={() => navigate("/forms/families")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <h2 className="font-bold mb-4">نسخه‌ها (Revisions)</h2>

        {isLoadingRevisions && <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری نسخهها...</p>}

        {!isLoadingRevisions && (
          <div className="table-container" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>شماره نسخه</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>وضعیت</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {(revisions?.results || []).length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      هیچ نسخهای برای این فرم ثبت نشده.
                    </td>
                  </tr>
                ) : (
                  revisions!.results.map((rev: any) => (
                    <tr key={rev.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "1rem" }}>نسخهٔ {rev.revision_no}</td>
                      <td style={{ padding: "1rem" }}>
                        <StatusBadge status={rev.status} />
                      </td>
                      <td style={{ padding: "1rem" }}>
                        {rev.status === "published" ? (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              className="btn btn-secondary"
                              style={{ fontSize: "0.85rem", padding: "0.3rem 0.75rem" }}
                              onClick={() => navigate(`/forms/fill/${rev.id}`)}
                            >
                              پر کردن فرم
                            </button>
                            {isDeploymentPhase && (
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: "0.85rem", padding: "0.3rem 0.75rem", background: "rgba(234, 179, 8, 0.2)", color: "#eab308", border: "1px solid rgba(234, 179, 8, 0.5)" }}
                                onClick={() => navigate(`/forms/revisions/${rev.id}`)}
                              >
                                ویرایش استقرار
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: "0.85rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => navigate(`/forms/revisions/${rev.id}`)}
                          >
                            {rev.status === "draft" ? "ویرایش" : "مشاهده"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    draft: { label: "پیشنویس", color: "#6B655A" },
    pending: { label: "در انتظار تأیید", color: "#BA9960" },
    published: { label: "منتشرشده", color: "#3B7A72" },
    rejected: { label: "رد شده", color: "#B3423A" },
  };
  const conf = map[status] || { label: status, color: "#6B655A" };
  return (
    <span
      style={{
        padding: "0.2rem 0.7rem",
        borderRadius: "999px",
        fontSize: "0.8rem",
        color: "#fff",
        background: conf.color,
      }}
    >
      {conf.label}
    </span>
  );
}
